import { SummaryResultNetworkType, SummaryResultScope, SummaryResultType } from "@app/shared/data-summaries";
import { BenchmarkingDashboardDetailsReportTypeEnum } from "@app/overviews/network-benchmarking-dashboard/dashboard-details";
import { ZipClassAnalysisSearchParameters, ZipCodeAnalysisSearchParameters } from "@app/projects/zip-projects/interfaces/zipAnalysisSearchParameters";
import { ProviderSetVersionSummaryType } from "@app/provider-sets/enumerations";
import { NetworkComparisonReportFiltersPayload } from "@app/overviews/network-benchmarking-dashboard/filters/network-comparison-filters/shared/NetworkComparisonReportFiltersPayload";
import { ChangedGapsEnum, EntityTypeEnum, LocationConfidenceScoresLabelEnum, NetworkAssessmentAnalysisType, NetworkIntegrityAggregateGroup, NextClosestProviderAction, PatientVolumeLabelEnum, ProviderActionStatusEnum, TimeFrequency } from "@app/projects/enumerators";
import { ProviderSearchByEnum, ProviderViewByEnum } from "@app/projects/network-assessment/provider-hub/interfaces";
import { ExceptionsFilterRequestModel } from "@app/exceptions/interfaces";
import { ZipAnalysisStateCountyPairParameters } from "@app/report-customization/build-reports/interfaces/zipAnalysisStateCountyPairParameters";
import { ProviderNetworkTypeEnum } from "@app/shared/enums/providerNetworkTypeEnum";
import { ExportFormatType, AdequacyAggregateGranularity, ExportGapFilterType, PractitionerStatuses, LocationStatuses, PractitionerAtLocationStatuses, NppesStatuses, AdequacyAggregateType, MembershipType, ExportProviderAvailabilityType, NetworkProviderComparisonType, NetworkProviderFilterType, ReportFormatType, ReportSettingsType } from "@app/exports/enumerations";
import { NetworkComparisonFiltersPayload } from "@app/overviews/network-benchmarking-dashboard/filters/network-comparison-filters/shared/NetworkComparisonFiltersPayload";
import { CareJourneyScoresValueEnum, EffectivenessEfficiencyValueEnum, MotiveScoreValueEnum, NewPatientClaimsEnum } from "@app/projects/servicing-provider";
import { ClaimsActivityLineOfBusinessTypeEnum, ClaimsActivityVolumeLabelEnum } from "@app/projects/network-assessment/pci-dashboard/claims-activity";

interface ExportBaseModel {
  formatType: ExportFormatType;
  exportName: string;
  eMailOnSuccess: boolean;
  runAsynchronous: boolean;
  sharedUsers?: number[];
  runFrequency?: TimeFrequency;
  scheduledAt?: string;
}

// Access Report
interface ExportAccessReportsUsageModel {
  clientId: number;
  endDate: Date;
  productTypeName: string;
  startDate: Date;
}

// Accuracy
interface ExportAccuracyAggregatesModel extends ExportBaseModel {
  customProjectId: number;
  projectRunConfigurationId: number;
  comparedProjectRunConfigurationId?: number;
  projectURL: string;

  networkAssessmentAnalysisType: NetworkAssessmentAnalysisType;
  aggregateGranularity: AdequacyAggregateGranularity;

  includeActiveRegionSets: boolean;
  includePendingRegionSets: boolean;
  regionSetGapFilter: ExportGapFilterType;

  includePhysicianSpecialties: boolean;
  includeFacilitySpecialties: boolean;
  specialtyGapFilter: ExportGapFilterType;

  specialtyGroupCodes: string[];
  inspectionUids?: string[];
  includeInspectorResultPercentageColumns: boolean;
  includeExpandedOutcomes: boolean;

  showDifferencesOnly?: boolean;

  stateFilters?: string[];
  countyFilters?: string[];
}

interface ExportAccuracySummariesModel extends ExportBaseModel {
  customProjectId: number;
  projectRunConfigurationId: number;
  projectURL: string;

  includeActiveRegionSets: boolean;
  includePendingRegionSets: boolean;
  regionSetGapFilter: ExportGapFilterType;

  includePhysicianSpecialties: boolean;
  includeFacilitySpecialties: boolean;
  specialtyGapFilter: ExportGapFilterType;

  regionSetIds: number[];
  specialtyGroupCodes: string[];
}

interface ExportAccuracyActionableProvidersModel extends ExportBaseModel {
  customProjectId: number;
  projectRunConfigurationId: number;
  comparedProjectRunConfigurationId?: number;
  projectURL: string;

  networkAssessmentAnalysisType: NetworkAssessmentAnalysisType;
  providerNetworkType: ProviderNetworkTypeEnum;

  includeActiveRegionSets: boolean;
  includePendingRegionSets: boolean;

  includePractitionerEntityType: boolean;
  includeServiceLocationEntityType: boolean;

  specialtyGroupCodes: string[];

  providerIds: number[];

  inspectionBand: string;
  inspectionUids: string[];

  recommendedActions: number[];
  recommendedActionReasons: string[];
  showAllRecommendedActionReasons: boolean;
  filterByTrueCounty: boolean;
  rowTypes: number[];
  practitionerAtLocationConfidenceScores: number[];
  pcaLabels: number[];

  stateFilters?: string[];
  countyFilters?: string[];
}

interface ExportAccuracyActionableProvidersBulkModel extends ExportBaseModel {
  customProjectIds: number[];
  projectRunConfigurationId: number;
  comparedProjectRunConfigurationId?: number;
  projectURL: string;
  separateReportOutput: boolean;
  allowParallelization?: boolean;
  publishedOnly: boolean;

  networkAssessmentAnalysisType: NetworkAssessmentAnalysisType;
  providerNetworkType: ProviderNetworkTypeEnum;

  includeActiveRegionSets: boolean;
  includePendingRegionSets: boolean;

  includePractitionerEntityType: boolean;
  includeServiceLocationEntityType: boolean;

  specialtyGroupCodes: string[];

  providerIds: number[];

  inspectionBand: string;
  inspectionUids: string[];

  recommendedActions: number[];
  recommendedActionReasons: string[];
  showAllRecommendedActionReasons: boolean;
  filterByTrueCounty: boolean;
  rowTypes: number[];
  practitionerAtLocationConfidenceScores: number[];
  pcaLabels: number[];

  stateFilters?: string[];
  countyFilters?: string[];
}

interface ExportStatusReportModel extends ExportBaseModel {
  customProjectId: number;
  projectRunConfigurationId: number;
  projectURL: string;

  networkAssessmentAnalysisType: NetworkAssessmentAnalysisType;
  providerNetworkType: ProviderNetworkTypeEnum;

  includeActiveRegionSets: boolean;
  includePendingRegionSets: boolean;

  inspectionBand: string;
  inspectionUids: string[];

  specialtyGroupCodes: string[];

  practitionerStatuses: PractitionerStatuses[];
  locationStatuses: LocationStatuses[];
  practitionerAtLocationStatuses: PractitionerAtLocationStatuses[];
  nppesStatuses: NppesStatuses[];

  providerIds: number[];
  stateFilters?: string[];
  countyFilters?: string[];
}

interface ExportRecommendedActionReasonModel {
  reason: string;
  friendlyName: string;
}

// Adequacy
interface ExportAdequacyAggregatesModel extends ExportBaseModel {
  customProjectId: number;
  projectRunConfigurationId: number;
  comparedProjectRunConfigurationId?: number;
  projectURL: string;

  networkAssessmentAnalysisTypes: NetworkAssessmentAnalysisType[];
  aggregateType: AdequacyAggregateType;
  aggregateGranularity: AdequacyAggregateGranularity;
  regionSetGapFilter: ExportGapFilterType;
  specialtyGapFilter: ExportGapFilterType;

  includeActiveRegionSets: boolean;
  includeApprovedRegionSets: boolean;
  includePendingRegionSets: boolean;
  includePhysicianSpecialties: boolean;
  includeFacilitySpecialties: boolean;

  regionSetIds: number[];
  specialtyGroupCodes: string[];
  reportCustomizationId?: number;
}

interface ExportAdequacyVolumeAggregateRequestModel extends ExportBaseModel {
  customProjectIds: number[];
  projectRunConfigurationId?: number;
  comparedProjectRunConfigurationId?: number;
  allowParallelization?: boolean;
  generateNoDataFoundReadme: boolean;
  publishedOnly: boolean;
  separateReportOutput?: boolean;
  volumeReportGroupMemorySize?: number;
  isVolumeReport: boolean;
  aggregateType: AdequacyAggregateType;
  aggregateGranularity: AdequacyAggregateGranularity;
  networkAssessmentAnalysisTypes: NetworkAssessmentAnalysisType[];
  includeActiveRegionSets: boolean;
  includeApprovedRegionSets: boolean;
  includePendingRegionSets: boolean;
  includeFacilitySpecialties: boolean;
  includePhysicianSpecialties: boolean;
  includeRequiredSpecialties: boolean;
  includeInformationalSpecialties: boolean;
  regionSetGapFilter: ExportGapFilterType;
  specialtyGapFilter: ExportGapFilterType;
  regionSetIds: number[];
  specialtyGroupCodes: string[];
  reportCustomizationId?: number;
}

interface ExportAdequacySummariesModel extends ExportBaseModel {
  customProjectId: number;
  projectRunConfigurationId: number;
  comparedProjectRunConfigurationId?: number;
  projectURL: string;

  networkAssessmentAnalysisType: NetworkAssessmentAnalysisType;

  includeActiveRegionSets: boolean;
  includeApprovedRegionSets: boolean;
  includePendingRegionSets: boolean;
  regionSetGapFilter: ExportGapFilterType;

  includeExpandableSpecialties: boolean;
  includePhysicianSpecialties: boolean;
  includeFacilitySpecialties: boolean;
  specialtyGapFilter: ExportGapFilterType;

  regionSetIds: number[];
  showCMSCredit: boolean;
  specialtyGroupCodes: string[];
  changedGapsOnly: boolean;

  reportCustomizationId?: number;
}

interface ExportAdequacySpecialtySummaryProjectModel extends ExportBaseModel {
  customProjectId: number;
  projectRunConfigurationId: number;
  comparedProjectRunConfigurationId?: number;
  projectURL: string;
  networkAssessmentAnalysisTypes: NetworkAssessmentAnalysisType[];
  includeActiveRegionSets: boolean;
  includeApprovedRegionSets: boolean;
  includePendingRegionSets: boolean;
  regionSetGapFilter: ExportGapFilterType;

  includePhysicianSpecialties: boolean;
  includeFacilitySpecialties: boolean;
  specialtyGapFilter: ExportGapFilterType;

  regionSetIds: number[];
  showExpandableSpecialties: boolean;
  specialtyGroupCodes: string[];
  changedGapsFilter: ChangedGapsEnum;
  membershipType: MembershipType;

  reportCustomizationId?: number;
}

interface ExportAdequacyServicingProvidersModel extends ExportBaseModel {
  customProjectId: number;
  projectRunConfigurationId: number;
  comparedProjectRunConfigurationId?: number;
  nextClosestProviderAction?: NextClosestProviderAction;
  projectURL: string;

  providerNetworkType: ProviderNetworkTypeEnum;
  networkAssessmentAnalysisTypes: NetworkAssessmentAnalysisType[];
  impactProviderType: ExportProviderAvailabilityType;
  providerComparisonTypes: NetworkProviderComparisonType[];

  includeActiveRegionSets: boolean;
  includeApprovedRegionSets: boolean;
  includePendingRegionSets: boolean;
  regionSetGapFilter: ExportGapFilterType;

  includePhysicianSpecialties: boolean;
  includeFacilitySpecialties: boolean;
  specialtyGapFilter: ExportGapFilterType;

  networkProviderFilterType: NetworkProviderFilterType;

  regionSetIds: number[];

  specialtyGroupCodes: string[];

  providerIds: number[];

  hsdFormat: ReportFormatType;
  appendPatternOfCareProviders: boolean;

  providerClaimsVolumeFilter: PatientVolumeLabelEnum;

  isZipAnalysis: boolean;
  reportCustomizationId?: number;
  includeCargoColumns?: boolean;
  stateCountyPairsParametersModel?: ZipAnalysisStateCountyPairParameters[];

  selectedCareJourneyCostValues?: CareJourneyScoresValueEnum[];
  selectedCareJourneyQualityValues?: CareJourneyScoresValueEnum[];
  selectedEffectivenessValues?: EffectivenessEfficiencyValueEnum[];
  selectedEfficiencyValues?: EffectivenessEfficiencyValueEnum[];
  selectedLineOfBusiness?: ClaimsActivityLineOfBusinessTypeEnum;
  selectedLocationConfidenceValues?: LocationConfidenceScoresLabelEnum[];
  selectedMotiveAppropriatenessValues?: MotiveScoreValueEnum[];
  selectedNewPatientClaimsValues?: NewPatientClaimsEnum[];
  selectedVolumeLabels?: ClaimsActivityVolumeLabelEnum[];
}

// based on ExportZipSummariesRequestModel on the API
// leaving out the properties that aren't used
interface ZipSummariesReportModel extends ExportBaseModel {
  changedGapsOnly: boolean;
  comparedProjectRunConfigurationId?: number;
  customProjectId: number;
  includeActiveRegionSets: boolean;
  includeApprovedRegionSets: boolean;
  includeFacilitySpecialties: boolean;
  includePendingRegionSets: boolean;
  includePhysicianSpecialties: boolean;
  networkAssessmentAnalysisTypes: NetworkAssessmentAnalysisType[];
  projectRunConfigurationId: number;
  projectUrl: string;
  regionSetGapFilter: ExportGapFilterType;
  regionSetIds: number[];
  reportCustomizationId?: number;
  specialtyGapFilter: ExportGapFilterType;
  specialtyGroupCodes: string[];
}

interface ClosestProviderReportModel extends ExportBaseModel {
  customProjectId: number;
  projectRunConfigurationId: number;
  projectUrl: string;
  regionSetIds: number[];
  reportCustomizationId?: number;
  networkAssessmentAnalysisType: NetworkAssessmentAnalysisType;
}

// Attestation
interface ExportAttestationAggregatesModel extends ExportBaseModel {
  customProjectId: number;
  projectRunConfigurationId: number;
  projectURL: string;

  includeActiveRegionSets: boolean;
  includeApprovedRegionSets: boolean;
  includePendingRegionSets: boolean;
  regionSetGapFilter: ExportGapFilterType;

  includePhysicianSpecialties: boolean;
  includeFacilitySpecialties: boolean;
  specialtyGapFilter: ExportGapFilterType;

  regionSetIds: number[];
  specialtyGroupCodes: string[];
}

interface ExportAttestationSummariesModel extends ExportBaseModel {
  customProjectId: number;
  projectRunConfigurationId: number;
  projectURL: string;

  includeActiveRegionSets: boolean;
  includeApprovedRegionSets: boolean;
  includePendingRegionSets: boolean;
  regionSetGapFilter: ExportGapFilterType;

  includePhysicianSpecialties: boolean;
  includeFacilitySpecialties: boolean;
  specialtyGapFilter: ExportGapFilterType;

  regionSetIds: number[];
  specialtyGroupCodes: string[];
}

interface ExportAttestationServicingProvidersModel extends ExportBaseModel {
  customProjectId: number;
  projectRunConfigurationId: number;
  comparedProjectRunConfigurationId?: number;
  projectURL: string;

  providerNetworkType: ProviderNetworkTypeEnum;

  includeActiveRegionSets: boolean;
  includeApprovedRegionSets: boolean;
  includePendingRegionSets: boolean;
  regionSetGapFilter: ExportGapFilterType;

  includePhysicianSpecialties: boolean;
  includeFacilitySpecialties: boolean;
  specialtyGapFilter: ExportGapFilterType;

  hsdFormat: ReportFormatType;

  regionSetIds: number[];

  specialtyGroupCodes: string[];

  providerIds: number[];
}

// Composite
interface ExportCompositeAggregatesModel extends ExportBaseModel {
  customProjectId: number;
  projectRunConfigurationId: number;
  projectURL: string;

  includeActiveRegionSets: boolean;
  includeApprovedRegionSets: boolean;
  includePendingRegionSets: boolean;
  regionSetGapFilter: ExportGapFilterType;

  includePhysicianSpecialties: boolean;
  includeFacilitySpecialties: boolean;
  specialtyGapFilter: ExportGapFilterType;

  regionSetIds: number[];
  specialtyGroupCodes: string[];
}

interface ExportCompositeSummariesModel extends ExportBaseModel {
  customProjectId: number;
  projectRunConfigurationId: number;
  projectURL: string;

  includeActiveRegionSets: boolean;
  includeApprovedRegionSets: boolean;
  includePendingRegionSets: boolean;
  regionSetGapFilter: ExportGapFilterType;

  includePhysicianSpecialties: boolean;
  includeFacilitySpecialties: boolean;
  specialtyGapFilter: ExportGapFilterType;

  regionSetIds: number[];
  specialtyGroupCodes: string[];
}

interface ExportCompositeServicingProvidersModel extends ExportBaseModel {
  customProjectId: number;
  projectRunConfigurationId: number;
  comparedProjectRunConfigurationId?: number;
  projectURL: string;

  providerNetworkType: ProviderNetworkTypeEnum;

  includeActiveRegionSets: boolean;
  includeApprovedRegionSets: boolean;
  includePendingRegionSets: boolean;
  regionSetGapFilter: ExportGapFilterType;

  includePhysicianSpecialties: boolean;
  includeFacilitySpecialties: boolean;
  specialtyGapFilter: ExportGapFilterType;

  hsdFormat: ReportFormatType;

  regionSetIds: number[];

  specialtyGroupCodes: string[];

  providerIds: number[];
}

// Network Integrity
interface ExportNetworkIntegrityProviderReportModel extends ExportBaseModel {
  projectId: number;
  projectRunConfigurationId: number;
  providerNetworkType: ProviderNetworkTypeEnum;
  includeCargoColumns: boolean;
  isServicing: boolean;
  aggregateGroup: NetworkIntegrityAggregateGroup;
  aggregateKey: string;
  npiEntityType?: EntityTypeEnum | null;
  selectedKeys?: string[] | null;
  showConfidenceLevel: boolean;
  selectedProviderAndProviderSetVersionIds: string[];
}

interface ExportNetworkIntegrityGapImpactReportModel extends ExportBaseModel {
  projectId: number;
  projectRunConfigurationId: number;
  providerType: string;
  impactProviderKey: string;
  regionSetIds: number[];
  specialtyCodes: string[];
  showNewGapsOnly: boolean;
  searchText: string;
  includeProviderDetails: boolean;
}

// Provider Hub
interface ExportProviderHubReportModel extends ExportBaseModel {
  projectId: number;
  projectRunConfigurationId: number;
  viewByOption: ProviderViewByEnum;
  searchByOption: ProviderSearchByEnum;
  searchByInput: string;
  serviceAreas: number[];
  specialtyGroupCodes: string[];
  providerNetworkTypes: string[];
  providerActionStatuses: ProviderActionStatusEnum[];
  gapClosedMin: number;
  gapClosedMax: number;
  accessPointCountMin: number;
  accessPointCountMax: number;
  effectiveness: (number | null)[];
  efficiency: (number | null)[];
  totalClaimsVolume: string[];
  totalNewPatientIndicator: (boolean | null)[];
  commercialClaimsVolume: string[];
  commercialNewPatientIndicator: (boolean | null)[];
  medicaidClaimsVolume: string[];
  medicaidNewPatientIndicator: (boolean | null)[];
  medicareClaimsVolume: string[];
  medicareNewPatientIndicator: (boolean | null)[];
  tricareClaimsVolume: string[];
  tricareNewPatientIndicator: (boolean | null)[];
  vaClaimsVolume: string[];
  vaNewPatientIndicator: (boolean | null)[];
  motiveScore: (number | null)[];
  careJourneyCostScore?: (number | null)[];
  careJourneyQualityScore?: (number | null)[];
  includeUnknownEffectiveness?: boolean;
  includeUnknownEfficiency?: boolean;
  includeUnknownMotiveScore?: boolean;
  includeUnknownCareJourneyCostScore?: boolean;
  includeUnknownCareJourneyQualityScore?: boolean;
  locationConfidence?: string[];
  networkIntegrityProjectOptionsExclusions: string[];
  networkIntegrityCriticalAlerts: string[];
  networkIntegrityAttestationBasedAlerts: string[];
  networkIntegrityExcessAddressesMin: number;
  networkIntegrityExcessAddressesMax: number;
  networkIntegrityExcessSpecialtiesMin: number;
  networkIntegrityExcessSpecialtiesMax: number;
  reportCustomizationId?: number;
}

// Zip Analysis
interface ExportZipAnalysisRequestModel extends ExportBaseModel {
  customProjectId: number;
  projectRunConfigurationId: number;
  excludeZeroMembership: boolean;
  includeExpandableSpecialties: boolean;
  includeFacilitySpecialties: boolean;
  includePhysicianSpecialties: boolean;
  regionSetIds: number[];
  specialtyGapFilter: ExportGapFilterType;
  regionSetGapFilter: ExportGapFilterType;
  specialtyGroupCodes: string[];
  networkAssessmentAnalysisType: NetworkAssessmentAnalysisType;
  searchParametersModel: ZipCodeAnalysisSearchParameters | ZipClassAnalysisSearchParameters;
  comparedProjectRunConfigurationId?: number;
  reportCustomizationId?: number;
  changedGapsFilter: ChangedGapsEnum;
  stateCountyPairsParametersModel?: ZipAnalysisStateCountyPairParameters[];
}

// User Login Statistics
interface ExportUserLoginStatisticsRequestModel extends ExportBaseModel {
  clientId: number;
  includeGuestLogins: boolean;
}

// Summaries
interface ExportSummariesDetailProvidersRequestModel extends ExportBaseModel {
  customProjectId: number;
  projectRunConfigurationId: number;
  projectURL: string;

  scope: SummaryResultScope;
  networkType: SummaryResultNetworkType;
  resultType: SummaryResultType;
  key: string;
  displayKey: string;
}

interface RegionSetFilter {
  networkAssessmentAnalysisType: NetworkAssessmentAnalysisType;
  secondaryNetworkAssessmentAnalysisType?: NetworkAssessmentAnalysisType;
  filterType: ExportGapFilterType;
  includeActive: boolean;
  includePending: boolean;
  includeApproved: boolean;
}

interface SpecialtyFilter {
  filterType: ExportGapFilterType;
  includeFacilitySpecialties: boolean;
  includePhysicianSpecialties: boolean;
  includeExpandableSpecialties: boolean;
}

interface EntityTypeFilter {
  includePractitionerEntityType: boolean;
  includeServiceLocationEntityType: boolean;
}

interface HSDFormatFilter {
  filterType: ReportFormatType;
  includeFacilitySpecialties: boolean;
  includePhysicianSpecialties: boolean;
}

interface ReportSettingsModel {
  id?: number;
  clientId?: number;
  userId?: number;
  type: ReportSettingsType;
  isDefault: boolean;
  name: string;
  reportSettingsJson: string;

  displayActions?: boolean;
  displayNameInput?: boolean;
  displayToDelete?: boolean;
}

interface VolumeSummariesRequestModel extends ExportBaseModel {
  customProjectIds: number[];
  networkAssessmentAnalysisType?: NetworkAssessmentAnalysisType;
  includeExceptionComment?: boolean;
  allowParallelization?: boolean;
  includeActiveRegionSets: boolean;
  includePendingRegionSets: boolean;
  includeApprovedRegionSets: boolean;
  regionSetGapFilter: ExportGapFilterType;
  regionSetIds: number[];
  specialtyGapFilter: ExportGapFilterType;
  specialtyGroupCodes: string[];
  separateReportOutput?: boolean;
  publishedOnly?: boolean;
  generateNoDataFoundReadme: boolean;
  isVolumeReport: boolean;
  sharedUsers: number[];
  reportCustomizationId?: number;
}

interface VolumeAdequacySpecialtySummariesRequestModel extends ExportBaseModel {
  customProjectIds: number[];
  networkAssessmentAnalysisTypes?: NetworkAssessmentAnalysisType[];
  allowParallelization?: boolean;
  includeActiveRegionSets: boolean;
  includePendingRegionSets: boolean;
  includeApprovedRegionSets: boolean;
  includeFacilitySpecialties: boolean;
  includePhysicianSpecialties: boolean;
  includeRequiredSpecialties: boolean;
  includeInformationalSpecialties: boolean;
  excludeZeroMembership: boolean;
  regionSetGapFilter: ExportGapFilterType;
  regionSetIds: number[];
  specialtyGapFilter: ExportGapFilterType;
  specialtyGroupCodes: string[];
  separateReportOutput?: boolean;
  publishedOnly?: boolean;
  generateNoDataFoundReadme: boolean;
  isVolumeReport: boolean;
  sharedUsers: number[];
  showExceptionComment?: boolean;
  membershipType: MembershipType;
  reportCustomizationId?: number;
}

interface ExportCmsExceptionReportRequestModel extends ExportBaseModel {
  documentType: string;
  exceptionRationale1: boolean | null;
  exceptionRationale2: boolean | null;
  exceptionRationale3: boolean | null;
  exceptionRationale4: boolean | null;
  specialtyGroupExceptionIds: number[];
  includeZeroPercentGainsProvidersData: boolean;
  zeroPercentGainsProvidersComment: string;
}

interface ExportCciioJustificationReportRequestModel extends ExportBaseModel {
  specialtyGroupExceptionIds: number[];
  includeZeroPercentGainsProvidersData: boolean;
  zeroPercentGainsProvidersComment?: string;
}

enum ExportHsdColumnsEnum {
  Unknown,
  AdditionalGeographic,
}

enum ExportImpactProviderTypeEnum {
  Unknown,
  Available,
  Targeted,
  Excluded,
  All,
  Changed,
}

enum ExportNextClosestProviderActionEnum {
  Unknown,
  None,
  FilterToNextClosestProviderOnly,
  AppendNextClosestProviders,
}

interface VolumeServicingProvidersRequestModel extends ExportBaseModel {
  customProjectIds: number[];
  publishedOnly: boolean;
  networkAssessmentAnalysisTypes: NetworkAssessmentAnalysisType[];
  providerNetworkTypes: ProviderNetworkTypeEnum[];
  hsdFormat: boolean;
  hsdColumns: ExportHsdColumnsEnum;
  includeCargoColumns: boolean;
  cargoColumnsToInclude: string[];
  networkProviderFilterType: NetworkProviderFilterType;
  impactProviderType: ExportImpactProviderTypeEnum;
  nextClosestProviderAction: ExportNextClosestProviderActionEnum;
  specialtyGapFilter: ExportGapFilterType;
  allowParallelization: boolean;
  separateReportOutput: boolean;
  generateNoDataFoundReadme: boolean;
  IncludeActiveRegionSets: boolean;
  IncludePendingRegionSets: boolean;
  IncludeApprovedRegionSets: boolean;
  regionSetGapFilter: ExportGapFilterType;
  IncludePhysicianSpecialties: boolean;
  IncludeFacilitySpecialties: boolean;
  IncludeRequiredSpecialties: boolean;
  IncludeInformationalSpecialties: boolean;
  regionSetIds: number[];
  specialtyGroupCodes: string[];
  sharedUsers: number[];
  isCCIIOPPFReport: boolean;
  isDistinctServicingProviderReport: boolean;
  membershipType?: MembershipType;
  appendPatternOfCareProviders: boolean;
  suppressHSDColumns: boolean;
  reportCustomizationId?: number;
  isZipAnalysis?: boolean;
}

interface ProviderSetVersionSelection {
  providerSetId: number;
  providerSetVersionId?: number;
}

interface ExportCCIIOProviderModel extends ExportBaseModel {
  includeInvalidCCIIORecords: boolean;
  providerSetVersionSelections: ProviderSetVersionSelection[];
}

interface ExportCCIIODataValidationsModel extends ExportBaseModel {
  providerSetVersionSelections: ProviderSetVersionSelection[];
  separateReportOutput: boolean;
}

interface SharedUserModel {
  userId: number;
  userName: string;
  email: string;
  fullName: string;
}

interface VolumeZipSummariesRequestModel extends ExportBaseModel {
  customProjectIds: number[];
  allowParallelization?: boolean;
  generateNoDataFoundReadme?: boolean;
  publishedOnly: boolean;
  separateReportOutput?: boolean;
  volumeReportGroupMemorySize?: number;

  networkAssessmentAnalysisType: NetworkAssessmentAnalysisType;
  includeActiveRegionSets: boolean;
  includeApprovedRegionSets: boolean;
  includePendingRegionSets: boolean;
  includeFacilitySpecialties: boolean;
  includePhysicianSpecialties: boolean;
  includeRequiredSpecialties: boolean;
  includeInformationalSpecialties: boolean;
  regionSetGapFilter: ExportGapFilterType;
  specialtyGapFilter: ExportGapFilterType;
  regionSetIds: number[];
  specialtyGroupCodes: string[];
  sharedUsers: number[];
  reportCustomizationId?: number;
}

interface ExportBulkSpecialtySummaryByZipRequestModel extends ExportBaseModel {
  allowParallelization: boolean;
  customProjectIds: number[];
  generateNoDataFoundReadme: boolean;
  includeActiveRegionSets: boolean;
  includeApprovedRegionSets: boolean;
  includeFacilitySpecialties: boolean;
  includeInformationalSpecialties: boolean;
  includePendingRegionSets: boolean;
  includePhysicianSpecialties: boolean;
  includeRequiredSpecialties: boolean;
  excludeZeroMembership: boolean;
  publishedOnly: boolean;
  regionSetGapFilter: ExportGapFilterType;
  regionSetIds: number[];
  reportCustomizationId?: number;
  separateReportOutput?: boolean;
  sharedUsers: number[];
  specialtyGapFilter: ExportGapFilterType;
  specialtyGroupCodes: string[];
}

interface ExportBulkZipClassAggregateResultsRequestModel extends ExportBaseModel {
  allowParallelization: boolean;
  customProjectIds: number[];
  generateNoDataFoundReadme: boolean;
  includeActiveRegionSets: boolean;
  includeApprovedRegionSets: boolean;
  includeFacilitySpecialties: boolean;
  includeInformationalSpecialties: boolean;
  includePendingRegionSets: boolean;
  includePhysicianSpecialties: boolean;
  includeRequiredSpecialties: boolean;
  publishedOnly: boolean;
  regionSetGapFilter: ExportGapFilterType;
  regionSetIds: number[];
  reportCustomizationId?: number;
  separateReportOutput?: boolean;
  sharedUsers: number[];
  specialtyGapFilter: ExportGapFilterType;
  specialtyGroupCodes: string[];
  zipClass?: string;
}

interface ExportPciCrossProjectRequestModel extends ExportBaseModel {
  customProjectIds: number[];
  separateReportOutput: boolean;
}

interface ExportBenchmarkingDashboardDetailsModel {
  filters: NetworkComparisonFiltersPayload;
  reportType: BenchmarkingDashboardDetailsReportTypeEnum;
  emailOnSuccess: boolean;
  sharedUsers: number[];
  exportName: string;
  formatType: ExportFormatType;
}

interface ExportBenchmarkingDashboardDetailsReportModel {
  filters: NetworkComparisonReportFiltersPayload;
  reportType: BenchmarkingDashboardDetailsReportTypeEnum;
  emailOnSuccess: boolean;
  sharedUsers: number[];
  exportName: string;
  formatType: ExportFormatType;
}

interface ExportProvidersModel {
  fileBaseName: string;
  providerFilter: string;
  providerSetId: number;
  providerSetVersionId: number;
  summaryType: ProviderSetVersionSummaryType;
}

interface ExportBulkSpecialtyGroupExceptionsModel extends ExportBaseModel {
  filters: ExceptionsFilterRequestModel
}

interface ExportNewProviderReportModel extends ExportBaseModel {
  customProjectIds: number[];
  reportingPeriodDays: number;
}

export {
  ExportAccessReportsUsageModel,
  ExportAccuracyAggregatesModel,
  ExportAccuracySummariesModel,
  ExportAccuracyActionableProvidersModel,
  ExportAccuracyActionableProvidersBulkModel,
  ExportRecommendedActionReasonModel,
  ExportAdequacyAggregatesModel,
  ExportAdequacySummariesModel,
  ExportAdequacyServicingProvidersModel,
  ExportAttestationAggregatesModel,
  ExportAttestationSummariesModel,
  ExportAttestationServicingProvidersModel,
  ExportCciioJustificationReportRequestModel,
  ExportCompositeAggregatesModel,
  ExportCompositeSummariesModel,
  ExportCompositeServicingProvidersModel,
  ExportNetworkIntegrityProviderReportModel,
  ExportNetworkIntegrityGapImpactReportModel,
  ExportNewProviderReportModel,
  ExportProviderHubReportModel,
  ExportStatusReportModel,
  ExportSummariesDetailProvidersRequestModel,
  ExportUserLoginStatisticsRequestModel,
  ExportZipAnalysisRequestModel,
  ExportAdequacyVolumeAggregateRequestModel,
  RegionSetFilter,
  ReportSettingsModel,
  SpecialtyFilter,
  EntityTypeFilter,
  HSDFormatFilter,
  ZipSummariesReportModel,
  ClosestProviderReportModel,
  VolumeServicingProvidersRequestModel,
  ExportNextClosestProviderActionEnum,
  ExportImpactProviderTypeEnum,
  VolumeSummariesRequestModel,
  VolumeZipSummariesRequestModel,
  ExportCCIIOProviderModel,
  ExportCCIIODataValidationsModel,
  ProviderSetVersionSelection,
  SharedUserModel,
  ExportPciCrossProjectRequestModel,
  ExportBenchmarkingDashboardDetailsModel,
  ExportBenchmarkingDashboardDetailsReportModel,
  ExportAdequacySpecialtySummaryProjectModel,
  VolumeAdequacySpecialtySummariesRequestModel,
  ExportCmsExceptionReportRequestModel,
  ExportBulkSpecialtyGroupExceptionsModel,
  ExportBulkSpecialtySummaryByZipRequestModel,
  ExportBulkZipClassAggregateResultsRequestModel,
  ExportProvidersModel
};